import {
  useToast,
  useDisclosure,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  FlexProps,
  Flex,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useMemo, useState } from 'react';

import {
  GetTasksBySectionDocumentGQLType,
  useBulkReassignTasksMutation,
  useGetOrganizationQuery,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { Counter } from 'packages/common-frontend/components/Counter';
import { SearchSelect } from 'packages/common-frontend/components/SearchSelect';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

export const BulkAssignTasks = ({
  taskIds,
  clearAll,
  selectAll,
  onSubmit = () => {},
  flexProps = {},
}: {
  taskIds: string[];
  clearAll?: () => void;
  selectAll?: () => void;
  onSubmit?: () => void;
  flexProps?: FlexProps;
}) => {
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [selectedUserId, setSelectedUserId] = useState<string>();

  const [
    bulkReassignTasksMutation,
    { loading: bulkReassignLoading },
  ] = useBulkReassignTasksMutation({
    refetchQueries: [GetTasksBySectionDocumentGQLType],
  });

  const { user } = useUserAuthOrThrow();

  const isAdmin = user.roleType === 'ADMIN';

  const getOrganization = useGetOrganizationQuery({
    skip: isAdmin === false,
    variables: { organizationId: user.organizationId },
  });

  const userOptions = useMemo(() => {
    if (
      getOrganization.data?.organization &&
      getOrganization.data.organization.users.length > 0
    ) {
      return getOrganization.data.organization.users.map((u) => ({
        label: `${u.firstName} ${u.lastName}`,
        value: u.id,
      }));
    }

    return null;
  }, [getOrganization.data?.organization]);

  if (getOrganization.error) {
    throw getOrganization.error;
  }

  if (!isAdmin || taskIds.length === 0 || !userOptions) {
    return null;
  }

  return (
    <>
      <Flex direction="row" gap="2rem" {...flexProps}>
        {clearAll && (
          <Button variant="link" colorScheme="blue" onClick={clearAll}>
            Clear all
          </Button>
        )}
        {selectAll && (
          <Button variant="link" colorScheme="blue" onClick={selectAll}>
            Select all
          </Button>
        )}
        <Button
          variant="secondary-muted"
          colorScheme="v2-aurora"
          onClick={onOpen}
          gap="0.75em"
        >
          Assign
          <Counter count={taskIds.length} />
        </Button>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Selected Tasks</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack gap="1em">
              <Typography fontWeight="medium">Select Assignee</Typography>
              <SearchSelect
                value={userOptions.find(
                  (option) => option.value === selectedUserId,
                )}
                onChange={(newUserId) => {
                  setSelectedUserId(newUserId);
                }}
                options={userOptions}
                isSelectedColor="lightgrey" // sarah todo: update SearchSelect component to operate on color scheme rather than  this
                isFocusedColor="lightgrey"
              />
            </Stack>
          </ModalBody>

          <ModalFooter gap="1em">
            <Button
              colorScheme="v2-aurora"
              variant="secondary"
              isDisabled={bulkReassignLoading}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              colorScheme="v2-aurora"
              isDisabled={bulkReassignLoading}
              disabled={selectedUserId === undefined}
              onClick={async () => {
                if (selectedUserId) {
                  try {
                    const result = await bulkReassignTasksMutation({
                      variables: {
                        taskIds,
                        newAssignedUserId: selectedUserId,
                      },
                    });
                    if (!result.data) {
                      throw (
                        result.errors ??
                        Error(
                          `Failed to bulk updated task user assignment [taskIds: ${taskIds}]`,
                        )
                      );
                    }
                    onSubmit();
                  } catch (err) {
                    Sentry.captureException(err);
                    toast({
                      title: 'Failed to bulk update task user assignment.',
                      status: 'error',
                      position: 'top',
                    });
                  } finally {
                    onClose();
                  }
                }
              }}
            >
              Assign Tasks
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
